"use client";

import { appConfig } from "@/constants";
import { usePrevious } from "@biom3/react";
import type * as Braze from "@braze/web-sdk";
import type React from "react";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { SubscriptionStatus } from "../types";
import { notifyError } from "../utils/monitoring";
import { registerServiceWorker } from "../utils/serviceWorker";
import { isImmutableUser, isSandbox } from "../utils/util";
import { usePassportProvider } from "./PassportProvider";
import { usePreferences } from "./PreferencesProvider";

const { BRAZE_API_ENDPOINT, BRAZE_API_KEY } = appConfig;

const CONFIG = {
  baseUrl: BRAZE_API_ENDPOINT,
  enableLogging: false,
  allowUserSuppliedJavascript: false,
  serviceWorkerLocation: "/service-worker.js",
  manageServiceWorkerExternally: true,
};

interface BrazeContextType {
  isInitialized: boolean;
  isPushSubscribed: boolean;
  isPushSupported: boolean;
  requestPushPermission: (
    onSuccess?: () => void,
    onError?: () => void,
  ) => Promise<void>;
  unregisterPush: (onSuccess: () => void, onError: () => void) => void;
  checkPushSubscriptionState: () => void;
  unsubscribe: () => void;
  optIn: () => void;
}

const BrazeContext = createContext<BrazeContextType | undefined>(undefined);

interface BrazeProviderProps {
  children: ReactNode;
}

export const BrazeProvider: React.FC<BrazeProviderProps> = ({ children }) => {
  const [braze, setBraze] = useState<typeof Braze | null>(null);
  const [brazeUser, setBrazeUser] = useState<Braze.User | undefined>();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isPushSubscribed, setIsPushSubscribed] = useState(false);
  const [isPushSupported, setIsPushSupported] = useState(false);

  const { userInfo } = usePassportProvider();
  const { data, update } = usePreferences();
  const previousUserInfo = usePrevious(userInfo);

  useEffect(() => {
    import("@braze/web-sdk").then(setBraze);
  }, []);

  const checkPushSubscriptionState = useCallback(() => {
    setIsPushSubscribed(braze?.isPushPermissionGranted() ?? false);
    setIsPushSupported(braze?.isPushSupported() ?? false);
  }, [braze]);

  // This runs only once when the Braze library is loaded.
  useEffect(() => {
    if (!braze || braze.isInitialized() || !BRAZE_API_KEY) {
      return;
    }

    const initializeBraze = async () => {
      try {
        await registerServiceWorker();

        braze.initialize(BRAZE_API_KEY, CONFIG);
        braze.openSession(); // Open a session for the anonymous user
        braze.automaticallyShowInAppMessages();

        checkPushSubscriptionState();
        setIsInitialized(true); // Signal that the SDK is ready
      } catch (error) {
        notifyError(error, "discoverFeedError");
      }
    };

    initializeBraze();
  }, [braze, checkPushSubscriptionState]);

  // This runs whenever the user's login status changes.
  useEffect(() => {
    // Wait for the SDK to be initialized and for user info to be available.
    if (!isInitialized || !braze || !userInfo) {
      return;
    }

    // Only identify if the user ID has actually changed from the previous state.
    if (userInfo.sub === previousUserInfo?.sub) {
      return;
    }

    // Filter out non-target users in sandbox
    if (isSandbox() && !isImmutableUser(userInfo)) return;

    braze.changeUser(userInfo.sub);
    const user = braze.getUser();
    if (user) {
      if (userInfo.email) {
        user.setEmail(userInfo.email);
      }
      setBrazeUser(user);
    } else {
      notifyError("Failed to get user after changeUser", "braze");
    }
  }, [isInitialized, braze, userInfo, previousUserInfo]);

  const handleRequestPushPermission = useCallback(
    async (onSuccess?: () => void, onError?: () => void) => {
      if (!braze?.isPushSupported()) {
        console.warn("Push notifications are not supported in this browser");
        return;
      }
      braze.requestPushPermission(
        () => {
          checkPushSubscriptionState();
          onSuccess?.();
        },
        () => {
          onError?.();
        },
      );
    },
    [braze, checkPushSubscriptionState],
  );

  const unregisterPush = useCallback(
    (onSuccess: () => void, onError: () => void) => {
      braze?.unregisterPush(onSuccess, onError);
    },
    [braze],
  );

  const updatePreference = useCallback(
    (marketing_consent: SubscriptionStatus) => {
      if (!userInfo) return;

      brazeUser?.setPushNotificationSubscriptionType(marketing_consent);
      brazeUser?.setEmailNotificationSubscriptionType(marketing_consent);
      update({ ...data, marketing_consent, onboard: false });
    },
    [userInfo, update, data, brazeUser],
  );

  const contextValue: BrazeContextType = {
    isInitialized,
    isPushSubscribed,
    isPushSupported,
    requestPushPermission: handleRequestPushPermission,
    checkPushSubscriptionState,
    unsubscribe: () => updatePreference("unsubscribed"),
    optIn: () => updatePreference("opted_in"),
    unregisterPush,
  };

  return (
    <BrazeContext.Provider value={contextValue}>
      {children}
    </BrazeContext.Provider>
  );
};

export const useBraze = (): BrazeContextType => {
  const context = useContext(BrazeContext);
  if (context === undefined) {
    throw new Error("useBraze must be used within a BrazeProvider");
  }
  return context;
};
